import { useCallback } from "react";
import { useNavigate, useParams, type NavigateOptions, type To } from "react-router-dom";
import { isSupportedLocale, pickDefaultLocale, withLocale } from "@/i18n/locale-utils";

/**
 * Drop-in replacement for `useNavigate` that auto-prepends the current
 * locale segment when called with a string/To path starting with "/".
 * External URLs, hashes, and relative paths pass through unchanged.
 */
export function useLocalizedNavigate() {
  const navigate = useNavigate();
  const { locale: paramLocale } = useParams<{ locale: string }>();
  const locale = isSupportedLocale(paramLocale) ? paramLocale : pickDefaultLocale();

  return useCallback(
    (to: To | number, options?: NavigateOptions) => {
      if (typeof to === "number") {
        navigate(to);
        return;
      }
      if (typeof to === "string") {
        if (
          to.startsWith("http://") ||
          to.startsWith("https://") ||
          to.startsWith("//") ||
          to.startsWith("mailto:") ||
          to.startsWith("tel:") ||
          to.startsWith("#") ||
          !to.startsWith("/")
        ) {
          navigate(to, options);
          return;
        }
        navigate(withLocale(locale, to), options);
        return;
      }
      // Object form
      if (to && typeof to === "object" && to.pathname && to.pathname.startsWith("/")) {
        navigate({ ...to, pathname: withLocale(locale, to.pathname) }, options);
        return;
      }
      navigate(to, options);
    },
    [navigate, locale],
  );
}
